import { DialogTrigger } from "@light-technology/components";
import { type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { type Role } from "../../state/api.types";
import { errorDetail } from "../../utilities/errors";
import { FormModal } from "../shared/FormModal";
import { RoleSelector } from "./RoleSelector";

type EditPermissionsForm = {
  role: Role;
};

type EditPermissionsModalProps = {
  displayName: string;
  currentRole: Role;
  isLoading: boolean;
  isSuccess: boolean;
  isFetching: boolean;
  error: unknown;
  onSubmit: (role: Role) => void;
  onReset: () => void;
  children: ReactNode;
};

export const EditPermissionsModal = ({
  displayName,
  currentRole,
  isLoading,
  isSuccess,
  isFetching,
  error,
  onSubmit,
  onReset,
  children,
}: EditPermissionsModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { handleSubmit, control, reset } = useForm<EditPermissionsForm>({
    defaultValues: {
      role: currentRole,
    },
  });

  // Auto-close on success
  const shouldClose = isSuccess && !isFetching;
  const [prevShouldClose, setPrevShouldClose] = useState(shouldClose);
  if (prevShouldClose !== shouldClose) {
    setPrevShouldClose(shouldClose);
    if (shouldClose) setIsOpen(false);
  }

  const handleFormSubmit = handleSubmit(({ role }) => {
    onSubmit(role);
  });

  const handleOpenChange = (open: boolean) => {
    if (open) {
      onReset();
      reset();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  // Extract general error message
  let updateError = null;
  if (error) {
    const errorMsg = errorDetail(error as FetchBaseQueryError);
    updateError = errorMsg ?? "Failed to update permissions. Please try again.";
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {children}
      <FormModal
        textTitle={`Which permissions should ${displayName} have?`}
        textCancel="Cancel"
        textSubmit="Save Changes"
        isLoading={isLoading || isFetching}
        onCancel={handleCancel}
        onSubmit={handleFormSubmit}
        data-ph-capture-attribute-action="edit-teammate-permissions"
      >
        <RoleSelector control={control} />
        {updateError && <div className="mt-2 text-center">{updateError}</div>}
      </FormModal>
    </DialogTrigger>
  );
};
